/**
 * PATH: packages/pricelist/src/columnRules.ts
 * FILE_ID: PKG-PRICELIST-RULES-001
 * PURPOSE: Pure validation of pricelist rows against a section's column rules
 *          (required, numeric min/max). Used to gate Save so invalid data never
 *          reaches the server, and to message the operator about what's wrong.
 * WHY: Operators asked for "rules on the pricelist and the way fields act" —
 *      required fields + bounded numbers are the data-quality guardrails.
 * RELATED: ColumnManagerDialog (sets rules), usePricelistWorkbook (save gate)
 */
import type { ColumnDef } from './types'
import type { GridRow } from './columns'

export interface RowValidationError {
  rowId: string
  key: string
  label: string
  message: string
}

const toNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

/** Validate one value against a column's rules. Returns an error message or null. */
export function validateColumnValue(col: ColumnDef, value: unknown): string | null {
  const empty = value === null || value === undefined || value === ''
  if (col.required && empty) return `${col.label} is required`
  if (empty) return null
  if (col.data_type === 'number' || col.data_type === 'money') {
    const n = toNum(value)
    if (n === null) return `${col.label} must be a number`
    if (typeof col.min === 'number' && n < col.min) return `${col.label} must be ≥ ${col.min}`
    if (typeof col.max === 'number' && n > col.max) return `${col.label} must be ≤ ${col.max}`
  }
  return null
}

/**
 * Validate rows against the section schema. Computed columns are skipped
 * (their values are derived, never user-entered).
 */
export function validateRows(rows: GridRow[], schema: ColumnDef[]): RowValidationError[] {
  const errors: RowValidationError[] = []
  for (const row of rows) {
    for (const col of schema) {
      if (col.computed_from) continue
      const message = validateColumnValue(col, row[col.key])
      if (message) {
        errors.push({ rowId: String(row.id), key: col.key, label: col.label, message })
      }
    }
  }
  return errors
}
